import { getMatchMin, TIER_OFFSETS } from './rules';

/**
 * Every tunable number the well's effect layer reads. Kept in one module so a
 * balance pass is a diff you can actually review, and so Forge upgrades (Act 2+)
 * can hand out a mutated copy without touching the resolver.
 */
export interface GemValues {
  readonly emberDamage: number;
  readonly frostBlock: number;
  readonly venomPoison: number;
  /** Volt gems required for one Energy. 3 base, 2 when forged. */
  readonly voltPerEnergy: number;
}

export const BASE_GEM_VALUES: GemValues = {
  emberDamage: 2,
  frostBlock: 2,
  venomPoison: 1,
  voltPerEnergy: 3,
};

/** Wave 1 ×1, wave 2 ×2, wave 3+ ×3. Chain Amulet raises this later. */
export const CASCADE_CAP = 3;

export function cascadeMultiplier(wave: number): number {
  return Math.min(Math.max(wave, 1), CASCADE_CAP);
}

/**
 * Burst damage for N full rows cleared in a single wave.
 *
 * Flat on purpose. The 44 at 4 rows is already a quarter of The Mason; scaling
 * it by cascade depth would let one four-row clear end a fight and make the table
 * meaningless. Gems *inside* those rows still fire at wave multiplier — that is
 * where the cascade payoff lives.
 */
export const ROW_BURST: readonly number[] = [0, 6, 14, 26, 44, 70];

/** Row counts above the table clamp to the last entry (5+ = 70). */
export function rowBurstDamage(rows: number): number {
  if (rows <= 0) return 0;
  const last = ROW_BURST.length - 1;
  return ROW_BURST[Math.min(rows, last)] ?? 0;
}

/**
 * Burst scaled by how much of the cleared row was actually gems.
 *
 * A row of seven Stone still has to leave the well — it is the only thing that
 * removes Stone — but an inert row is not damage. So the table value is taxed
 * by the row's real-gem proportion. Seven colored gems pay the full table; six
 * gems and a Stone pay 6/7 of it; a solid Stone row pays nothing.
 */
export function proportionalRowBurst(rows: number, realGems: number, rowWidth: number): number {
  if (rows <= 0) return 0;
  const capacity = rows * rowWidth;
  if (capacity <= 0) return 0;
  return Math.floor((rowBurstDamage(rows) * realGems) / capacity);
}

/**
 * Match size changes the KIND of payoff, not just the amount.
 *
 *   3      base
 *   4      CHARGED  per-gem value doubles
 *   5      SURGE    charged, and Ember/Venom hit every living enemy
 *   6+     AETHER   surge, and every other gem of that color detonates too
 *
 * Aether's flooded gems fire at BASE value, not charged — the run itself stays
 * the special event, the flood is the reward, and a double multiplier on a
 * double multiplier is how a well-balanced table stops being balanced.
 */
export type MatchTier = 'base' | 'charged' | 'surge' | 'aether';

/**
 * Absolute tier thresholds derived from the current matchMin.
 *
 * The ladder is offset-based so raising the threshold carries every tier with
 * it. At matchMin 3: base=3, charged=4, surge=5, aether=6. At matchMin 4:
 * base=4, charged=5, surge=6, aether=7 — a full-column run on a 7-wide well.
 */
export function tierThreshold(): Record<MatchTier, number> {
  const base = getMatchMin();
  return {
    base,
    charged: base + TIER_OFFSETS.charged,
    surge: base + TIER_OFFSETS.surge,
    aether: base + TIER_OFFSETS.aether,
  };
}

const TIER_RANK: Record<MatchTier, number> = { base: 0, charged: 1, surge: 2, aether: 3 };

export function tierFor(size: number): MatchTier {
  const t = tierThreshold();
  if (size >= t.aether) return 'aether';
  if (size >= t.surge) return 'surge';
  if (size >= t.charged) return 'charged';
  return 'base';
}

export function tierRank(tier: MatchTier): number {
  return TIER_RANK[tier];
}

/** Charged and above double the per-gem value of the run's own cells. */
export function tierGemMultiplier(tier: MatchTier): number {
  return tier === 'base' ? 1 : 2;
}

/** True when this tier routes its Ember/Venom at every living enemy. */
export function tierHitsAll(tier: MatchTier): boolean {
  return tier === 'surge' || tier === 'aether';
}
